import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Breadcrumb, Card, Switch, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import Swal from 'sweetalert2';

import { adminService } from '../../services/admin.service';

interface ScheduledJobSetting {
    keySetting: string;
    enabled: boolean;
}

interface JobRow {
    keySetting: string;
    name: string;
    interval: string;
    description: string;
    enabled: boolean;
}

const JOBS = [
    {
        keySetting: 'JOB_UPDATE_BALANCE_PROVIDER',
        name: 'Cập nhật số dư nhà cung cấp',
        interval: '60 giây',
        description: 'Lấy số dư mới nhất từ các nhà cung cấp đang hoạt động.',
    },
    {
        keySetting: 'JOB_REMINDER_BALANCE_PROVIDER',
        name: 'Cảnh báo số dư nhà cung cấp và partner',
        interval: '60 giây',
        description: 'Cảnh báo số dư thấp của nhà cung cấp và partner.',
    },
    {
        keySetting: 'JOB_RUN_FOR_TEST',
        name: 'Xử lý giao dịch test',
        interval: '30 giây',
        description: 'Hoàn tất các giao dịch đang xử lý trong môi trường test.',
    },
    {
        keySetting: 'JOB_UPDATE_TRANSACTION_PENDING',
        name: 'Cập nhật giao dịch pending',
        interval: '90 giây',
        description: 'Kiểm tra trạng thái mới nhất của giao dịch airtime đang xử lý.',
    },
    {
        keySetting: 'JOB_TRANSACTION_TIMEOUT',
        name: 'Cảnh báo giao dịch quá thời gian',
        interval: '90 giây',
        description: 'Gửi cảnh báo khi có giao dịch xử lý quá thời gian cho phép.',
    },
];

export const SystemSettingsPage = () => {
    const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
    const [maintenanceLoading, setMaintenanceLoading] = useState(false);
    const [settings, setSettings] = useState<Record<string, boolean>>({});
    const [loading, setLoading] = useState(false);
    const [updatingKey, setUpdatingKey] = useState<string | null>(null);

    const loadMaintenanceSetting = async () => {
        setMaintenanceLoading(true);
        try {
            const response = await adminService.getModeSystem();
            setMaintenanceEnabled(response.data.valueSetting === '1');
        } catch (error: any) {
            message.error(error?.response?.data?.detail || 'Không thể tải trạng thái bảo trì hệ thống');
        } finally {
            setMaintenanceLoading(false);
        }
    };

    const loadSettings = async () => {
        setLoading(true);
        try {
            const response = await adminService.getScheduledJobSettings();
            const values = (response.data || []).reduce(
                (result: Record<string, boolean>, item: ScheduledJobSetting) => {
                    result[item.keySetting] = item.enabled;
                    return result;
                },
                {}
            );
            setSettings(values);
        } catch (error: any) {
            message.error(error?.response?.data?.detail || 'Không thể tải cài đặt hệ thống');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadMaintenanceSetting();
        loadSettings();
    }, []);

    const updateMaintenanceMode = async (enabled: boolean) => {
        const action = enabled ? 'bật' : 'tắt';
        const result = await Swal.fire({
            title: `Xác nhận ${action} chế độ Bảo Trì`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: action.toUpperCase(),
            cancelButtonText: 'Hủy',
            reverseButtons: true,
        });

        if (!result.isConfirmed) {
            return;
        }

        setMaintenanceLoading(true);
        try {
            const response = await adminService.updateModeSystem(enabled ? '1' : '0');
            setMaintenanceEnabled(response.data.valueSetting === '1');
            message.success({ content: 'Cập nhật trạng thái hệ thống thành công', duration: 3 });
        } catch (error: any) {
            message.error(error?.response?.data?.detail || 'Không thể cập nhật trạng thái bảo trì hệ thống');
        } finally {
            setMaintenanceLoading(false);
        }
    };

    const updateSetting = async (keySetting: string, enabled: boolean) => {
        setUpdatingKey(keySetting);
        try {
            const response = await adminService.updateScheduledJobSetting(keySetting, enabled);
            setSettings(current => ({
                ...current,
                [response.data.keySetting]: response.data.enabled,
            }));
            message.success(enabled ? 'Đã bật job' : 'Đã tắt job');
        } catch (error: any) {
            message.error(error?.response?.data?.detail || 'Không thể cập nhật cài đặt job');
        } finally {
            setUpdatingKey(null);
        }
    };

    const rows = useMemo<JobRow[]>(
        () => JOBS.map(job => ({ ...job, enabled: settings[job.keySetting] === true })),
        [settings]
    );

    const columns: ColumnsType<JobRow> = [
        {
            title: 'Job',
            dataIndex: 'name',
            render: (name: string, row) => (
                <>
                    <Typography.Text strong>{name}</Typography.Text>
                    <br />
                    <Typography.Text type='secondary'>{row.description}</Typography.Text>
                </>
            ),
        },
        {
            title: 'Chu kỳ',
            dataIndex: 'interval',
            width: 130,
            align: 'center',
            render: (interval: string) => <Tag color='blue'>{interval}</Tag>,
        },
        {
            title: 'Trạng thái',
            dataIndex: 'enabled',
            width: 150,
            align: 'center',
            render: (enabled: boolean, row) => (
                <Switch
                    checked={enabled}
                    loading={updatingKey === row.keySetting}
                    disabled={updatingKey !== null && updatingKey !== row.keySetting}
                    checkedChildren='Đang chạy'
                    unCheckedChildren='Đã tắt'
                    onChange={value => updateSetting(row.keySetting, value)}
                />
            ),
        },
    ];

    return (
        <>
            <Breadcrumb items={[{ title: 'Hệ thống' }, { title: 'Cài đặt hệ thống' }]} />
            <Card title='Chế độ bảo trì hệ thống' style={{ marginTop: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                    <div>
                        <Typography.Text strong>Chế độ bảo trì</Typography.Text>
                        <br />
                        <Typography.Text type='secondary'>
                            Khi bật, hệ thống sẽ từ chối các giao dịch mới theo quy tắc bảo trì hiện tại.
                        </Typography.Text>
                    </div>
                    <Switch
                        checked={maintenanceEnabled}
                        loading={maintenanceLoading}
                        checkedChildren='Bảo trì'
                        unCheckedChildren='Hoạt động'
                        onChange={updateMaintenanceMode}
                    />
                </div>
            </Card>
            <Card title='Cài đặt Schedule Job' style={{ marginTop: 16 }}>
                <Alert
                    type='info'
                    showIcon
                    message='Thay đổi có hiệu lực từ lượt chạy kế tiếp, không cần khởi động lại API.'
                    style={{ marginBottom: 16 }}
                />
                <Table<JobRow>
                    rowKey='keySetting'
                    loading={loading}
                    columns={columns}
                    dataSource={rows}
                    pagination={false}
                />
            </Card>
        </>
    );
};
